import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LifecycleStage, LifecycleService } from './lifecycle.service';
import { AuthContext } from '../../../context/authcontext';

interface LeadFormData {
  lifecycleStages: LifecycleStage[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

const LeadFormDataContext = createContext<LeadFormData | undefined>(undefined);

interface LeadFormDataProviderProps {
  children: ReactNode;
  isMainApiCompleted?: boolean;
  shouldLoad?: boolean;
}

export const LeadFormDataProvider: React.FC<LeadFormDataProviderProps> = ({ children, isMainApiCompleted = false, shouldLoad = true }) => {
  const { selectedTenantId } = useContext(AuthContext);

  const [lifecycleStages, setLifecycleStages] = useState<LifecycleStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = async () => {
    if (loading || !selectedTenantId || !isMainApiCompleted) return;

    setLoading(true);
    setError(null);

    try {
      const stagesData = await LifecycleService.getLeadStages(parseInt(selectedTenantId));

      setLifecycleStages(stagesData);
      setDataLoaded(true);
    } catch (err) {
      console.error('Error fetching lead form data:', err);
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setDataLoaded(false);
    await fetchData();
  };

  // Fetch data when tenant changes or on first load - only run when main API has completed and the data is actually needed
  useEffect(() => {
    if (selectedTenantId && isMainApiCompleted && shouldLoad && !dataLoaded) {
      fetchData();
    }
  }, [selectedTenantId, isMainApiCompleted, shouldLoad, dataLoaded]);

  const value: LeadFormData = {
    lifecycleStages,
    loading,
    error,
    refreshData,
  };

  return (
    <LeadFormDataContext.Provider value={value}>
      {children}
    </LeadFormDataContext.Provider>
  );
};

export const useLeadFormData = (): LeadFormData => {
  const context = useContext(LeadFormDataContext);
  if (!context) {
    throw new Error('useLeadFormData must be used within a LeadFormDataProvider');
  }
  return context;
};
